/**
 * PO Line Dashboard — the PO-line-grain sibling of the Scheduled Dashboard.
 *
 * Backed by ``analytics/po_line_dashboard``. Same period blocks as the
 * scheduled payload (current_week / ytd / per_week / per_month), so the
 * period types are shared; delivery is judged per PurchaseOrderLine +
 * GoodsReceiptNote and money is held in the customer's reporting currency
 * (``currency``).
 */
import api, { type AxiosResponse } from './client';
import type {
  CurrentWeekData,
  DeliveryMetrics,
  MonthTrendPoint,
  PeriodData,
  WeekTrendPoint,
} from './scheduled-dashboard';

export type { CurrentWeekData, DeliveryMetrics, MonthTrendPoint, PeriodData, WeekTrendPoint };

/** One Mon–Sun (tenant week-start) slice of the selected month, clamped to the month. */
export interface POLineDeliveryByWeek {
  /** Day-of-month span, e.g. "3–9" or "31". */
  label: string;
  week_start: string;
  week_end: string;
  on_time: number;
  early: number;
  late: number;
  /** PO lines due in the week that still owe quantity. */
  pending: number;
}

/** Current Month block — shaped like ``CurrentWeekData`` over a calendar month. */
export interface CurrentMonthData extends PeriodData {
  /** OTD (GRN) window: month start → today while live, the full month once closed. */
  otif_window_start: string;
  otif_window_end: string;
  frozen: boolean;
  /** "August 2026" */
  month_label: string;
  month_start: string;
  month_end: string;
  /** 0 = live month, -1 = last month, … */
  selected_month_offset: number;
  is_live: boolean;
  deliveries_by_week: POLineDeliveryByWeek[];
}

export interface POLinePortfolio {
  /** Forward open value (delivery_date ≥ today, still owing). */
  open_orders_value: number;
  /** Cumulative arrears — every past-due open line beyond the buffer. */
  arrears_value: number;
  arrears_lines: number;
}

export interface POLineDashboardData {
  /** ``supplier_uploads`` when the figures come from the monthly supplier files (no order values). */
  source?: 'supplier_uploads';
  /** The PRIMARY customer (settings, currency). In a GROUP view the figures
   *  roll up every id in ``customer_ids`` — never scope a drill to this alone. */
  customer_id: number;
  /** Every customer rolled into the figures (one in SITE view). */
  customer_ids?: number[];
  /** Which kind of view produced the figures. */
  scope?: 'site' | 'group';
  supplier_id: number | null;
  part_number: string | null;
  today: string;
  /** ISO code every money figure in the payload is expressed in. */
  currency: string;
  delivery_metric_mode?: 'both' | 'otif_only' | 'otd_only';
  otif_target?: number;
  otd_target?: number;
  arrears_buffer_days?: number;
  portfolio: POLinePortfolio;
  current_week: CurrentWeekData;
  current_month: CurrentMonthData;
  /** W3-T12: fiscal year start → today; ``window_start`` is the fiscal year start (ISO date). */
  ytd: PeriodData & { window_start?: string; window_end?: string };
  per_week: { weeks: WeekTrendPoint[] };
  per_month: { months: MonthTrendPoint[] };
}

export interface POLineDashboardParams {
  customer_id?: number;
  /** The top view switcher's selection; takes the place of ``customer_id``. */
  group?: number;
  company?: number;
  site?: number;
  supplier_id?: number;
  part_number?: string;
  week_offset?: number;
  /** 0 = live month, negative = past months (clamped to -120). */
  month_offset?: number;
  display_currency?: string;
}

/** One dashboard figure → the rows behind it. Mirrors ``drilldown.ALL_MODES``. */
export type POLineDrilldownMode =
  | 'due' | 'open' | 'received' | 'arrears'
  | 'otif_passed' | 'otif_failed' | 'otif_arrears' | 'otif_pending'
  | 'otif_due' | 'otd_met'
  // Customer review 11 Sep (item 31/32): the OTD tile's denominator (same
  // rows as ``otif_due``) and its complement — due = met + missed.
  | 'otd_due' | 'otd_missed'
  | 'grn_all' | 'grn_on_time' | 'grn_early' | 'grn_late'
  | 'ncrs' | 'ppm'
  | 'appeals';

export type POLineDrilldownEntity = 'line' | 'grn' | 'ncr' | 'appeal';

export interface POLineDrilldownParams {
  mode: POLineDrilldownMode;
  customer_id?: number | string;
  group?: number;
  company?: number;
  site?: number;
  supplier_id?: number | string;
  part_number?: string;
  start?: string;
  end?: string;
  /** 'week' | 'month' — the opening tile's bucket grain (W3-T7: a week carries no arrears stock). */
  grain?: 'week' | 'month';
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface POLineDrilldownLineRow {
  /** The ODD the OTIF verdict and arrears use (backend purchasing/line_odd.py). */
  odd_date?: string | null;
  id: number;
  po_id: number;
  po_number: string;
  line_number: number;
  part_number: string;
  description: string;
  supplier_name: string | null;
  delivery_date: string | null;
  supplier_promise_date: string | null;
  quantity: number;
  received_qty: number;
  outstanding_qty: number;
  status: string;
  otif_status: string;
  is_otif: boolean;
  otif_overridden: boolean;
  /** CR-6191: fail reviewed and accepted as-is (verdict unchanged). */
  otif_acknowledged: boolean;
  acknowledge_status: string | null;
  previous_acknowledge_status: string | null;
  days_early_late: number | null;
  on_hold: boolean;
  last_receipt_date: string | null;
  unit_value: number;
  line_value: number;
  po_currency: string;
  /** OTIF decision actions the requesting user may take on this row (admins only). */
  can_override_otif: boolean;
  can_acknowledge_otif: boolean;
  can_reset_otif: boolean;
}

export interface POLineDrilldownGrnRow {
  /** The line's ODD — at PO-line grain the date this GRN's verdict used. */
  odd_date?: string | null;
  id: number;
  grn_number: string;
  receipt_date: string | null;
  quantity_received: number;
  /** PO line ordered quantity (the GRN's parent line). */
  ordered_qty: number;
  on_time_status: string | null;
  po_line_id: number;
  po_id: number;
  po_number: string;
  line_number: number;
  part_number: string;
  description: string;
  supplier_name: string | null;
  delivery_date: string | null;
}

export interface POLineDrilldownNcrRow {
  id: number;
  ncr_number: string | null;
  ncr_date: string | null;
  part_number: string;
  description: string;
  supplier_name: string | null;
  reject_qty: number;
  status: string;
  po_line_id: number | null;
}

export interface POLineDrilldownAppealRow {
  id: number;
  grn_id: number;
  grn_number: string | null;
  created_at: string;
  status: string;
  is_customer_appeal: boolean;
  qty_claimed: number | null;
  date_claimed: string | null;
  po_number: string | null;
  line_number: number | null;
  part_number: string | null;
  supplier_name: string | null;
  po_line_id: number | null;
}

export type POLineDrilldownRow =
  | POLineDrilldownLineRow
  | POLineDrilldownGrnRow
  | POLineDrilldownNcrRow
  | POLineDrilldownAppealRow;

export interface POLineDrilldownResponse {
  mode: POLineDrilldownMode;
  entity: POLineDrilldownEntity;
  count: number;
  page: number;
  page_size: number;
  currency: string;
  results: POLineDrilldownRow[];
}

// ============================================================================
// Forecast Stability (FORECAST-FLUCTUATION) — forward forecast movement
// ============================================================================

export type ForecastStabilityGrain = 'week' | 'month';

/** One forward delivery bucket: current forecast + what changed vs the baseline copy. */
export interface ForecastForwardBucket {
  /** ISO start of the bucket: Monday for weeks, the 1st for months. */
  bucket: string;
  /** Forecast qty currently due in the bucket. */
  current: number;
  /** Qty added to the bucket since the baseline copy. */
  added: number;
  /** Qty removed from the bucket since the baseline copy. */
  removed: number;
}

export interface ForecastStabilityData {
  /** 'snapshot' when at least one site in scope has a copy taken before its current import; 'none' otherwise. */
  source: 'snapshot' | 'none';
  grain: ForecastStabilityGrain;
  /** ISO date the live forecast was last imported — the point the baseline is chosen relative to. */
  anchor: string;
  /** ISO date of the most recent weekly copy; null before the first run. */
  last_captured: string | null;
  /** Primary site (its clock anchors "today"). */
  customer_id: number;
  /** Every PO-line-grain site rolled into the figures (one in a SITE view). */
  customer_ids: number[];
  /** Per-site live import dates. */
  anchors: Record<string, string>;
  forward: {
    /** ISO date of the most recent copy the deltas compare against; null when no site has one. */
    baseline: string | null;
    /** Per-site copy date, null for a site with no usable copy. */
    baselines: Record<string, string | null>;
    buckets: ForecastForwardBucket[];
  };
}

export interface ForecastStabilityParams {
  customer_id?: number | string;
  supplier_id?: number | string;
  part_number?: string;
  grain?: ForecastStabilityGrain;
}

export const poLineDashboardAPI = {
  /**
   * Forecast Stability: forecast qty per forward bucket and the qty added /
   * removed per bucket since the copy taken before the current import, per site (PO-line-grain customers).
   *
   * @param params - scope / filter plus `grain` ('month' default, or 'week')
   * @returns The bucketed movement payload.
   */
  getForecastStability(params?: ForecastStabilityParams): Promise<AxiosResponse<ForecastStabilityData>> {
    return api.get('/analytics/forecast-stability/', { params });
  },

  /**
   * Dashboard KPIs at PO-line grain.
   *
   * @param params - scope / filter / week selector
   * @returns The period-bucketed payload.
   */
  get(params?: POLineDashboardParams): Promise<AxiosResponse<POLineDashboardData>> {
    return api.get('/analytics/po-line-dashboard/', { params });
  },

  /**
   * Rows behind one figure, built from the same predicates as the tile.
   *
   * @param params - mode, period and the dashboard filter scope
   * @returns Paginated rows for the mode's entity.
   */
  drilldown(params: POLineDrilldownParams): Promise<AxiosResponse<POLineDrilldownResponse>> {
    return api.get('/analytics/po-line-dashboard/drilldown/', { params });
  },

  /**
   * Every row behind one figure as an XLSX blob (same predicates, unpaginated).
   *
   * @param params - mode, period and the dashboard filter scope
   * @returns The workbook as a Blob.
   */
  drilldownExport(params: Omit<POLineDrilldownParams, 'page' | 'page_size'>): Promise<AxiosResponse<Blob>> {
    return api.get('/analytics/po-line-dashboard/drilldown/export/', { params, responseType: 'blob' });
  },
};
